import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { 
  Package, Clock, DollarSign, TrendingUp, Users, Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function RestaurantDashboard() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const todayOrders = orders.filter(o => {
    const orderDate = new Date(o.created_at);
    const today = new Date();
    return orderDate.toDateString() === today.toDateString();
  });

  const pendingOrders = orders.filter(o => ['pending', 'confirmed', 'preparing'].includes(o.status));
  const completedOrders = orders.filter(o => o.status === 'delivered');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.restaurant_earnings || 0), 0);

  const stats = [
    { title: t('dashboard.todaysOrders'), value: todayOrders.length, icon: Package, color: 'text-blue-500', bg: 'bg-blue-50' },
    { title: t('dashboard.pendingOrders'), value: pendingOrders.length, icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50' },
    { title: t('dashboard.totalRevenue'), value: `£${totalRevenue.toFixed(2)}`, icon: DollarSign, color: 'text-green-500', bg: 'bg-green-50' },
    { title: t('dashboard.completedOrders'), value: completedOrders.length, icon: TrendingUp, color: 'text-purple-500', bg: 'bg-purple-50' },
  ];

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-purple-100 text-purple-700',
    ready: 'bg-indigo-100 text-indigo-700',
    in_transit: 'bg-orange-100 text-orange-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">{t('restaurantAdmin.dashboard')}</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="dashboard-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recentOrders')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : pendingOrders.length > 0 ? (
            <div className="space-y-3">
              {pendingOrders.slice(0, 5).map((order) => (
                <div 
                  key={order.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`order-${order.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-slate-500">
                      {order.items.length} {t('dashboard.items')} • {order.customer_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold text-slate-900">£{order.total.toFixed(2)}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status]}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">{t('dashboard.noPendingOrders')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
